import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRosterAvailableWrestlers } from '../../utils/wrestlerAvailability';

const normalizeGender = (gender) => {
  if (gender === 'Uomo') return 'M';
  if (gender === 'Donna') return 'F';
  return gender || 'M';
};

const defaultRenderCard = (wrestler) => {
  let titles = [];
  if (wrestler.championTypes && Array.isArray(wrestler.championTypes) && wrestler.championTypes.length > 0) {
    titles = wrestler.championTypes;
  } else if (wrestler.currentChampionship) {
    titles = [wrestler.currentChampionship];
  } else if (wrestler.championship) {
    titles = [wrestler.championship];
  } else if (wrestler.isChampion) {
    titles = ['Campione'];
  }

  const formatTitle = (title) => title.replace(/\bChampion\b/gi, '').trim();

  return (
    <Link to={`/wrestlers/${wrestler.id}`} className="text-decoration-none d-block">
      <article
        className="manage-roster-availability-card manage-roster-availability-card--responsive overflow-hidden"
        key={wrestler.id}
      >
        <div className="manage-roster-availability-card__row d-flex align-items-center gap-3 w-100">
          {wrestler.imageUrl ? (
            <img
              src={wrestler.imageUrl}
              alt={wrestler.name}
              className="rounded-circle flex-shrink-0 manage-roster-availability-card__avatar"
            />
          ) : (
            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 manage-roster-availability-card__avatar manage-roster-availability-card__avatar--placeholder">
              <i className="bi bi-person-fill fs-4" />
            </div>
          )}

          <div className="flex-grow-1 min-w-0">
            <h3 className="mb-0 fs-6 fw-semibold text-light manage-roster-availability-card__name">
              {wrestler.name}
            </h3>
            <div className="manage-roster-availability-card__titles mt-1">
              {titles.map((title, idx) => (
                <span
                  key={idx}
                  className="badge bg-brand-secondary text-dark manage-roster-availability-card__title-badge"
                  title={formatTitle(title)}
                >
                  {formatTitle(title)}
                </span>
              ))}
            </div>
          </div>

          <div className="text-end flex-shrink-0 manage-roster-availability-card__score">
            <span className="fw-bold text-light manage-roster-availability-card__score-value">
              {wrestler.score || 0}
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

const WrestlerAvailabilityList = ({
  wrestlers = [],
  factions = [],
  title = 'Wrestler liberi',
  defaultExpanded = false,
  showFilters = false,
  includeAssigned = false,
  onlyMainRoster = false,
  onlyNXT = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [genderFilter, setGenderFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');

  const availableWrestlers = useMemo(() => {
    return getRosterAvailableWrestlers({
      wrestlers,
      factions,
      includeAssigned,
      onlyMainRoster,
      onlyNXT,
    })
      .map((wrestler) => ({
        id: wrestler.id,
        name: wrestler.name || wrestler.nome || '-',
        imageUrl: wrestler.imageUrl || '/placeholder-wrestler.jpg',
        score: wrestler.score || 0,
        brand: wrestler.brand || 'Indy',
        gender: normalizeGender(wrestler.gender),
        alignment: wrestler.alignment || '',
        championTypes: Array.isArray(wrestler.championTypes) ? wrestler.championTypes.filter(Boolean) : [],
        currentChampionship: wrestler.currentChampionship || '',
        championship: wrestler.championship || '',
        isChampion: Boolean(
          wrestler.isChampion ||
            wrestler.currentChampionship ||
            wrestler.championship ||
            (Array.isArray(wrestler.championTypes) && wrestler.championTypes.some(Boolean))
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'it'));
  }, [factions, includeAssigned, onlyMainRoster, onlyNXT, wrestlers]);

  const availableBrands = useMemo(
    () => [...new Set(availableWrestlers.map((wrestler) => wrestler.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'it')),
    [availableWrestlers]
  );

  const filteredWrestlers = useMemo(() => {
    let filtered = availableWrestlers;

    if (searchTerm.trim()) {
      filtered = filtered.filter((wrestler) =>
        wrestler.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (showFilters && genderFilter !== 'all') {
      filtered = filtered.filter((wrestler) => wrestler.gender === genderFilter);
    }

    if (showFilters && brandFilter !== 'all') {
      filtered = filtered.filter((wrestler) => wrestler.brand === brandFilter);
    }

    return filtered;
  }, [availableWrestlers, brandFilter, genderFilter, searchTerm, showFilters]);

  const copyNames = async () => {
    if (!filteredWrestlers.length) {
      setCopyMessage('Nessun wrestler da copiare');
      setTimeout(() => setCopyMessage(''), 2500);
      return;
    }

    try {
      await navigator.clipboard.writeText(filteredWrestlers.map((wrestler) => wrestler.name).join('\n'));
      setCopyMessage(`${filteredWrestlers.length} nomi copiati`);
    } catch (error) {
      setCopyMessage('Errore durante la copia');
    }

    setTimeout(() => setCopyMessage(''), 2500);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setGenderFilter('all');
    setBrandFilter('all');
  };

  return (
    <section className="manage-roster-section">
      <div className="manage-roster-section__header">
        <div className="d-flex align-items-center justify-content-between gap-2 w-100 mb-2">
          <button
            type="button"
            className="btn btn-outline-brand-secondary flex-shrink-0"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
          </button>

          <h2 className="text-center fw-semibold mb-0" style={{ fontSize: '1.25rem' }}>
            {title} ({availableWrestlers.length})
          </h2>

          <button
            type="button"
            className="btn btn-outline-brand-primary-light flex-shrink-0"
            onClick={copyNames}
            disabled={!filteredWrestlers.length}
            title="Copia nomi wrestler"
          >
            <i className="bi bi-copy" />
          </button>
        </div>

        {isExpanded && (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
            {showFilters && (
              <>
                <select
                  className="form-select"
                  value={genderFilter}
                  onChange={(event) => setGenderFilter(event.target.value)}
                  style={{ width: 'auto', minWidth: '120px' }}
                >
                  <option value="all">Tutti i sessi</option>
                  <option value="M">Uomo</option>
                  <option value="F">Donna</option>
                </select>

                <select
                  className="form-select"
                  value={brandFilter}
                  onChange={(event) => setBrandFilter(event.target.value)}
                  style={{ width: 'auto', minWidth: '140px' }}
                >
                  <option value="all">Tutti i brand</option>
                  {availableBrands.map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </>
            )}

            <input
              type="search"
              className="form-control"
              placeholder="Cerca wrestler"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{ width: 'auto', minWidth: '180px', flex: '1 1 180px' }}
            />

            {(searchTerm || genderFilter !== 'all' || brandFilter !== 'all') && (
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={resetFilters}
              >
                <i className="bi bi-x-circle me-2" />
                Reset
              </button>
            )}
          </div>
        )}

        {copyMessage && (
          <div className="alert alert-success mt-3 mb-0 py-2">
            {copyMessage}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="mt-3">
          {filteredWrestlers.length ? (
            <div className="manage-roster-grid manage-roster-grid--compact">
              {filteredWrestlers.map(defaultRenderCard)}
            </div>
          ) : (
            <div className="manage-roster-empty manage-roster-empty--inline">
              <i className={`bi ${availableWrestlers.length ? 'bi-search' : 'bi-people-slash'}`} />
              <p>
                {availableWrestlers.length
                  ? showFilters
                    ? 'Nessun wrestler trovato per i filtri selezionati'
                    : `Nessun wrestler trovato per "${searchTerm}".`
                  : 'Nessun wrestler attivo disponibile.'}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default WrestlerAvailabilityList;
